/**
 * Fonte única de identidade do usuário logado (ADR-025). Estado resolvido 1x no
 * bootstrap (`main.ts`, antes de qualquer rota montar) e lido daqui em diante —
 * nunca refetch, nunca reatividade. Substitui as 4 declarações independentes de
 * `MeResponse` e as 3 origens de fetch de `/api/me/` encontradas na auditoria que
 * motivou a ADR (`main.ts`, `dashboard.tsx`, `account-popover.ts`, `admin-perfil.tsx`).
 *
 * Espelha 1:1 o payload de `backend/apps/accounts/views.py::me`.
 */
export interface MeResponse {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  telefone: string | null;
  role: string;
  is_superadmin: boolean;
  /** Tier dentro de role=admin (hierarquia Quadro→Gestor→Operador, ADR-032).
   * Ambos `false` para role=user/superadmin — só Admin Gestor (`is_gestor`)
   * pode sub-delegar; Admin Operador (`is_operador`) perde as ações de
   * delegação no frontend (ver main.ts, grupos.tsx, usuarios/index.tsx). */
  is_gestor: boolean;
  is_operador: boolean;
  documento: string;
  tipo_documento: string;
  sim_cpf: string | null;
  logo_empresa: string | null;
}

/**
 * Contrato de `denied` em `GET /api/permissions/me/` (ClickUp #868kt003c,
 * evolução "módulo não liberado/não utilizável"). Reflexo de uma hierarquia
 * de 3 papéis com delegação em cascata — oculto vs. inativo NÃO é mais
 * configuração de página escolhida pelo superadmin; é derivado do papel do
 * usuário logado, decidido no backend:
 * - Super Admin (Quadro): sempre acesso completo, nunca aparece em `denied`.
 * - Admin Gestor (`role=admin`): módulo que a Quadro não lhe atribuiu chega
 *   aqui como visível-mas-inativo, com `reason` (upsell/teaser).
 * - Admin Operador (`role=user`): `denied` sempre vazio — o que o Gestor não
 *   liberou simplesmente não aparece, sem teaser.
 * Não é lista exaustiva do catálogo: nunca derive dela "quais módulos existem".
 */
export interface DeniedEntry {
  /** Copy comercial escrita pelo superadmin (Catálogo — `denial_reason`). */
  reason: string;
  /** "not_enabled" = módulo não habilitado nesta instalação; "no_permission" =
   * módulo habilitado, mas o perfil não tem a permissão. Mantido só para
   * depuração/telemetria — como `denied` só chega a `role=admin`, as duas
   * causas significam a mesma coisa para o usuário ("a Quadro não te
   * atribuiu este módulo") e não escolhem mensagens diferentes. */
  cause: "not_enabled" | "no_permission";
}
export type DeniedMap = Record<string, DeniedEntry>;

/**
 * Chrome de UI (não conteúdo de negócio) — únicas 2 exceções autorizadas à
 * regra de "nada hardcoded" deste projeto (ver tarefa ClickUp #868kt003c):
 * - `DEFAULT_DENIAL_REASON`: fallback de `resolveDenialReason` quando o
 *   superadmin deixou `denial_reason` vazio no Catálogo. Cópia provisória —
 *   nenhuma copy comercial foi definida pelo produto para este fallback;
 *   pendente de revisão de texto por quem edita a copy comercial.
 * - `ROUTE_ACCESS_DENIED_MESSAGE`: toast genérico do guard de rota
 *   (main.ts::guarded()) para quando o codename nem aparece em `denied` —
 *   caso normal do Admin Operador (`role=user`), que nunca vê teaser.
 */
export const DEFAULT_DENIAL_REASON = "";
export const ROUTE_ACCESS_DENIED_MESSAGE = "Você não tem permissão para acessar esta página.";

/** Único ponto que decide o texto exibido a partir de uma entrada de `denied`
 * — consumido tanto pela sidebar (nav-config.ts::filterNav) quanto pelo
 * guard de rota (main.ts::guarded()), para nunca divergir entre os dois. */
export function resolveDenialReason(entry: DeniedEntry): string {
  return entry.reason || DEFAULT_DENIAL_REASON;
}

let identity: MeResponse | null = null;
let permissions: Set<string> = new Set();
let denied: DeniedMap = {};

/** Chamado uma única vez por `main.ts`, logo após o bootstrap resolver `/api/me/`
 * e `/api/permissions/me/`. */
export function setIdentity(me: MeResponse, perms: Set<string>, deniedMap: DeniedMap = {}): void {
  identity = me;
  permissions = perms;
  denied = deniedMap;
}

/** Lança se chamado antes do bootstrap resolver — não deveria acontecer em uso
 * normal, já que `main.ts` chama `setIdentity` antes de montar qualquer rota. */
export function getIdentity(): MeResponse {
  if (!identity) {
    throw new Error("getIdentity() chamado antes do bootstrap resolver a identidade (setIdentity ainda não foi chamado).");
  }
  return identity;
}

export function getPermissions(): Set<string> {
  return permissions;
}

export function getDenied(): DeniedMap {
  return denied;
}

/**
 * Home real do papel do usuário logado — destino de fallback tanto do
 * dispatch pós-login (`main.ts::resolveHardRedirect`, `/painel/` →) quanto do
 * guard de rota (`main.ts::guarded()`, acesso negado a uma página →). Único
 * ponto que decide essa regra: os dois pontos de chamada reaproveitam esta
 * função em vez de duplicar o `is_superadmin || role === "admin"` (ClickUp
 * #868kt003c — bug de cascata causado por exatamente esse tipo de duplicação,
 * ver histórico de `filterNav` em `nav-config.ts` para outro caso do mesmo
 * problema).
 */
export function homePathForRole(me: Pick<MeResponse, "is_superadmin" | "role">): string {
  // ADR-038 (achado crítico análogo ao de `cpf_login`/`post_login_redirect`
  // no backend, não coberto pelo ADR original): Operador ganhou `role`
  // próprio ("operator") — sem este ajuste, ele caía em "/app/dashboard" (a
  // home do cotista) em vez do painel administrativo.
  return me.is_superadmin || me.role === "admin" || me.role === "operator" ? "/painel/admin/" : "/app/dashboard";
}

/** Resultado de `resolveAccessDenial` — ver ali. `redirectTo: null` é o caso
 * degenerado (nenhuma rota do papel sobra pra onde ir). */
export interface AccessDenialAction {
  toastMessage: string;
  redirectTo: string | null;
}

/** Compara dois paths ignorando barra final ("/painel/admin" === "/painel/admin/")
 * — as duas rotas de home aceitam a barra opcional (`/app/dashboard` casa via
 * `/^\/app\/dashboard\/?$/`; `/painel/admin/` é registrada com barra, mas o
 * path do browser podia chegar sem ela). Comparação por IGUALDADE, não
 * prefixo: `/painel/admin/` é path-prefix de muitas outras rotas admin (ex.
 * `/painel/admin/api-docs/`), então `startsWith` trataria qualquer subrrota
 * negada como "já é a home" — exatamente o cenário real do bug ClickUp
 * #868kt003c reproduzido em `/painel/admin/api-docs/`. */
function isSamePath(a: string, b: string): boolean {
  const normalize = (p: string) => (p.endsWith("/") ? p : `${p}/`);
  return normalize(a) === normalize(b);
}

/**
 * Decide o que fazer quando `guarded()` (main.ts) nega acesso a uma rota:
 * qual toast mostrar e para onde redirecionar. Extraído de `guarded()` para
 * ficar testável sem depender do bootstrap de `main.ts` (fetch de `/api/me/`
 * etc.) — pura, sem I/O.
 *
 * `redirectTo === null` é o caso degenerado (ClickUp #868kt003c): a rota
 * negada já É a home derivada do papel (`currentPath` é a própria
 * `homePathForRole(me)`, não apenas uma subrrota dela), ou seja, não sobra
 * nenhuma rota do próprio papel pra onde mandar o usuário sem repetir a mesma
 * negação em loop. Quem chama decide o que fazer com `redirectTo === null` —
 * hoje, `guarded()` só mostra o toast e não desloga (deslogar é reservado ao
 * logout voluntário, nunca a este caso).
 */
export function resolveAccessDenial(
  me: Pick<MeResponse, "is_superadmin" | "role">,
  currentPath: string,
  deniedEntry: DeniedEntry | undefined,
): AccessDenialAction {
  const home = homePathForRole(me);
  const toastMessage = deniedEntry ? resolveDenialReason(deniedEntry) : ROUTE_ACCESS_DENIED_MESSAGE;
  const redirectTo = isSamePath(currentPath, home) ? null : home;
  return { toastMessage, redirectTo };
}

/** Primeiro nome + primeiro token do sobrenome (fallback para username) — usado
 * onde quer que o nome do usuário logado seja exibido (Home, AccountPopover). */
export function getDisplayName(me: MeResponse): string {
  const firstName = me.first_name || "";
  const firstSurname = me.last_name ? me.last_name.split(" ")[0] : "";
  return firstSurname ? `${firstName} ${firstSurname}` : firstName || me.username;
}
